import * as http from "../../utils/http";

// Saves a Care+ plan / one-time / prescription-only choice on the cart (Choose Plan + reorder).
// Goes through cart/update_subscription_data, which validates the plan and attaches its bonus.

export interface CarePlusPlanChoice {
  /** null = one-time or prescription-only. */
  planID: number | null;
  prescriptionOnly?: boolean;
  /** Only cart/update_subscription_data (guests, reorder). No prescription-only support. */
  singleWrite?: boolean;
  /** HL-12122: A/B arms stored on cart.options.experiments (for the server-side events). */
  experiments?: Record<string, string>;
}

export interface CarePlusCartApi {
  getCart: () => any;
  saveCart: (cart: any) => void;
  activateSubscriptionInDB: (subscriptionPlanID: number) => Promise<any>;
  deactivateSubscriptionInDB: () => Promise<any>;
}

/** Writes the server's cart view into the cart context; returns the merged cart. */
export const applyCartPayload = (payload: any, saveCart: CarePlusCartApi["saveCart"]): any => {
  const serverCart = payload?.cart ?? payload?.data?.cart;
  const products = payload?.products ?? payload?.data?.products;
  if (!serverCart) return null;
  const merged = {
    ...serverCart,
    products: products ?? serverCart.products,
    timekitBooking: serverCart?.timekitBooking || payload?.timekitBooking || null,
  };
  saveCart(merged);
  // getCart() still returns the old snapshot in this tick.
  return merged;
};

export const persistCarePlusPlanChoice = (
  { planID, prescriptionOnly = false, singleWrite = false, experiments }: CarePlusPlanChoice,
  { getCart, saveCart, activateSubscriptionInDB, deactivateSubscriptionInDB }: CarePlusCartApi
): Promise<void> => {
  const oneTime = planID === null;

  // Only cart/update-delivery-option persists selected_service_option and re-flags the products
  // (cart/update drops it). Also used to reset a stale prescription-only choice to "regular".
  const applyDeliveryOption = (deliveryOptionType: "only_prescription" | "regular") =>
    http
      .postToServer(
        "cart/update-delivery-option",
        { deliveryOptionType, isRxUpsell: false },
        [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID],
      )
      .then((response: any) => {
        const cartView = response?.data?.cartView;
        if (cartView?.cart) {
          saveCart({
            ...cartView.cart,
            products: cartView.products,
            timekitBooking: cartView.timekitBooking || null,
          });
        }
      });

  const currentCart = getCart();
  const needsDeliveryOptionReset =
    !prescriptionOnly && (currentCart?.selected_service_option === "only_prescription" || currentCart?.only_prescription);

  // Cart-token auth only, so it works for guests too. Saves the plan (or clears it), attaches its
  // bonus and the delivery intent in one write.
  const persistChoice = () =>
    http.postToServer(
      "cart/update_subscription_data",
      {
        subscriptionPlanID: planID,
        ...(experiments && Object.keys(experiments).length ? { experiments } : {}),
        // No intent sent = keep the cart's current delivery choice.
        ...(oneTime
          ? { onlyPrescription: prescriptionOnly }
          : needsDeliveryOptionReset
            ? { onlyPrescription: false }
            : {}),
      },
      [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID],
    ).then((response: any) => {
      // A refused plan can still answer 200; treat it as a failure.
      if (response?.data?.forceRedirect || response?.data?.status === "error") {
        throw new Error(response?.data?.code || "subscription_persist_failed");
      }
      applyCartPayload(response?.data, saveCart);
      return response;
    });

  const request = singleWrite
    ? persistChoice()
    : persistChoice().then(() =>
        oneTime
          ? deactivateSubscriptionInDB().then((res) =>
              prescriptionOnly
                ? applyDeliveryOption("only_prescription").then(() => res)
                : needsDeliveryOptionReset
                  ? applyDeliveryOption("regular").then(() => res)
                  : res,
            )
          : activateSubscriptionInDB(planID!).then((res) =>
              needsDeliveryOptionReset ? applyDeliveryOption("regular").then(() => res) : res,
            ),
      );

  return request.then((response: any) => {
    // The write returns the cart view; no re-fetch needed.
    if (response?.data?.cartView?.cart) {
      applyCartPayload(
        {
          cart: response.data.cartView.cart,
          products: response.data.cartView.products,
          timekitBooking: response.data.cartView.timekitBooking,
        },
        saveCart,
      );
    } else if (response?.data?.cart) {
      applyCartPayload(response.data, saveCart);
    }
  });
};
